import { Customer } from "@/shared/interfaces/customer/Customer.interface";

import { cloneDeep } from "lodash";

import { pruneEmptyObject } from "@/shared/scripts/helpers/object";

export function cleanCustomerObject(customer: Customer): Record<string, unknown> {
    const cleanedCustomer = cloneDeep(customer);

    const streetNumber = customer.location?.address?.provided?.streetNumber;
    if (streetNumber) {
        cleanedCustomer.location.address.provided.street += ", " + streetNumber;
        delete cleanedCustomer.location.address.provided.streetNumber;
    }

    const box = customer.location?.address?.provided?.box;
    if (box) {
        cleanedCustomer.location.address.provided.street += ", " + box;
        delete cleanedCustomer.location.address.provided.box;
    }

    const contactDetails = customer.contactDetails;
    if (contactDetails) {

        const landline = contactDetails.landline;
        const mobile = contactDetails.mobile;

        if (landline) {
            if (!landline.number) {
                delete cleanedCustomer.contactDetails.landline;
            }
        }

        if (mobile) {
            if (!mobile.number) {
                delete cleanedCustomer.contactDetails.mobile;
            }
        }
    }

    if (!customer.location?.address?.provided?.country) {
        cleanedCustomer.location = {
            address: {
                provided: {
                    country: "BE",
                },
            },
        };
    }

    if (customer.id) {
        delete cleanedCustomer.id;
    }

    if (customer.userCorrelationId) {
        delete cleanedCustomer.userCorrelationId;
    }

    if (customer.legacyCustomerId) {
        delete cleanedCustomer.legacyCustomerId;
    }

    if (customer.hasPayment) {
        delete cleanedCustomer.hasPayment;
    }

    if (customer.isNew) {
        delete cleanedCustomer.isNew;
    }

    return pruneEmptyObject(cleanedCustomer);
}

/**
 * A customer name is only "complete" for the B2C → Salesforce log when it holds both a
 * first and a last name, i.e. a non-empty value containing at least one space. Social/SSO
 * logins often yield a single-word username (no space), which is treated as incomplete and
 * triggers the MissingCustomerNameModal on the Payment step.
 */
export function isIncompleteCustomerName(name?: string | null): boolean {
    const trimmedName = name ? name.trim() : "";
    return !trimmedName || trimmedName.indexOf(" ") === -1;
}

/**
 * True once the customer object is the real record fetched from the API rather than the
 * `{ id }` placeholder several flows commit before the GET resolves (see ClassifiedsApi and
 * VisibilityUpgrade). `hasInvoicingEnabled` is the flag the rest of the codebase already uses
 * for this test, and it survives cleanCustomerObject because `false` is not pruned.
 */
export function isCustomerHydrated(customer?: Customer | null): boolean {
    return !!customer && "hasInvoicingEnabled" in customer;
}

/**
 * True when `customer.name` holds a COMPANY name rather than a person's name.
 *
 * `customer.name` is the invoicing name: the only input bound to it is CompanyName.vue
 * (`pathInStore: "customer.name"`), which renders only inside the invoicing accordion. A
 * company name is legitimately allowed to be a single word ("Telenet"), which the lexical
 * first-and-last-name rule in isIncompleteCustomerName would otherwise flag as incomplete.
 *
 * The vatNumber clause is deliberate: CustomerModal never clears name/vatNumber when the
 * invoicing radio is switched back off, so a leftover VAT number still marks a company record.
 */
export function holdsCompanyName(customer?: Customer | null): boolean {
    if (!customer) {
        return false;
    }

    if (customer.hasInvoicingEnabled === true) {
        return true;
    }

    return !!(customer.vatNumber && customer.vatNumber.trim());
}

/**
 * True when this customer must be asked for a first and last name before an order is created.
 *
 * Creating the order is what emits the B2C log to Salesforce, which rejects a blank Name with
 * REQUIRED_FIELD_MISSING (IO-154). The guard deliberately fails open for records it cannot or
 * must not repair: unhydrated placeholders and company records.
 *
 * IO-276: `hasPayment` is deliberately NOT a carve-out here. The customers PUT lambda's
 * notModifiableFieldsAfterPayment is
 * ["location.address.provided.country", "hasInvoicingEnabled", "vatNumber", "legacyCustomerId",
 * "hasPayment"] — `name` is absent from it, so the backend does accept a name change after
 * payment. Skipping paid customers would silently exclude repeat publishers, who are exactly the
 * population still carrying the `name: " "` records the old username fallback wrote.
 */
export function requiresPersonName(customer?: Customer | null): boolean {
    return isCustomerHydrated(customer)
        && !holdsCompanyName(customer)
        && isIncompleteCustomerName(customer ? customer.name : null);
}

/**
 * The machine placeholder applyCustomerNameFallback falls back on. Extracted so the two callers
 * cannot drift: whatever we write here has to stay recognisable afterwards (see
 * isPlaceholderCustomerName).
 */
export function getEmailLocalPart(email?: string | null): string {
    return typeof email === "string" ? email.split("@")[0].trim() : "";
}

/**
 * True when `name` is the placeholder we wrote ourselves rather than anything a human supplied.
 *
 * The name prompt must never seed its inputs from it. The user would have to clear it by hand,
 * and — worse — appending a surname to it yields a name that contains a space, so it satisfies
 * isIncompleteCustomerName, passes the guard, and reaches Salesforce as the customer's name.
 */
export function isPlaceholderCustomerName(name?: string | null, email?: string | null): boolean {
    const trimmedName = typeof name === "string" ? name.trim().toLowerCase() : "";
    const placeholder = getEmailLocalPart(email).toLowerCase();

    return !!trimmedName && !!placeholder && trimmedName === placeholder;
}

/**
 * Resolves the name to persist: what the form collected, else the portal username, else the
 * local part of the user's email.
 *
 * Never leaves a blank-but-present name behind: pruneEmptyObject drops `""` but keeps `" "`,
 * because lodash `isEmpty(" ")` is false — which is precisely how `name: " "` reached Salesforce
 * in IO-154.
 *
 * IO-276: it cannot simply drop the key either. The customers POST/PUT lambdas validate against
 * customers-management-schema.json, which has `name` in `required` with `minLength: 1`, so an
 * absent (or empty) name is a 400 — and for a private individual `name` is never form-collected
 * (CustomerForm binds it only inside the invoicing accordion), so the fallback IS the only
 * source. Hence the email local part: it satisfies minLength, and because it holds no space
 * `isIncompleteCustomerName` still flags it, so the guard in shared/mixins/missingCustomerName.ts
 * replaces it with a real first and last name before the order — and therefore before the B2C log
 * is emitted. Should it ever slip through, Salesforce accepts it, which beats rejecting the
 * record outright.
 */
export function applyCustomerNameFallback(
    customer: Customer,
    username?: string | null,
    email?: string | null
): void {
    const providedName = typeof customer.name === "string" ? customer.name.trim() : "";

    if (providedName) {
        customer.name = providedName;
        return;
    }

    const fallbackName = typeof username === "string" ? username.trim() : "";

    if (fallbackName) {
        customer.name = fallbackName;
        return;
    }

    const emailLocalPart = getEmailLocalPart(email);

    if (emailLocalPart) {
        customer.name = emailLocalPart;
        return;
    }

    // Unreachable for an authenticated caller — the POST needs a principal, and the backend takes
    // the email from that same authorizer. Left as a deliberate 400 rather than an invented
    // literal, so a genuinely nameless payload surfaces as a validation error instead of junk.
    // `name` is declared non-optional on Customer, so narrow to Partial rather than widen it.
    delete (customer as Partial<Customer>).name;
}

export function removeUnexpectedCustomerProperties(customer: Customer): void {
    [
        "creationDate",
        "lastAction",
        "lastActionDate",
        "loggedUser",
        "hasPayment",
    ].forEach(prop => delete customer[prop]);
}
